import React, { useState, useEffect } from 'react';
import { Clock, Save, ArrowLeft, Plus, Trash2, Copy, Calendar, PhoneCall, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { businessHoursApi, BusinessHoursDTO, BusinessHoursSyncReport } from '../../services/ivrApi';
import OrganizationFilter from '../../components/Filters/OrganizationFilter';
import { useScopedOrgId } from '../../hooks/useScopedOrgId';

// Day mapping for backend
const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
const DAY_LABELS: Record<typeof DAYS[number], string> = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

interface TimeSlot {
  startTime: string;
  endTime: string;
}

/**
 * A day, and every window it is open.
 *
 * A day is routinely split — 10:00–11:00 and again 11:00–20:00, or a morning and
 * an evening shift with a break between. This used to hold ONE window, so a split
 * day could only be saved by flattening it into a single window that claimed to
 * be open through the gap.
 *
 * `enabled: false` and an empty `slots` mean the same thing to the backend, which
 * stores a closed day as an empty list; `enabled` is kept here only so unticking
 * a day does not throw away the times somebody typed.
 */
interface DaySchedule {
  enabled: boolean;
  slots: TimeSlot[];
}

interface WeekSchedule {
  MONDAY: DaySchedule;
  TUESDAY: DaySchedule;
  WEDNESDAY: DaySchedule;
  THURSDAY: DaySchedule;
  FRIDAY: DaySchedule;
  SATURDAY: DaySchedule;
  SUNDAY: DaySchedule;
}

type AfterHoursAction = 'HANGUP' | 'VOICEMAIL' | 'TRANSFER' | 'CUSTOM';

interface BusinessHoursConfig {
  id?: number;
  timezone: string;
  schedule: WeekSchedule;
  enabled: boolean;
  afterHoursMessage: string;
  afterHoursAction: AfterHoursAction;
  afterHoursTransferNumber: string;
  afterHoursAudioUrl: string;
}

const defaultSchedule: WeekSchedule = {
  MONDAY: { enabled: true, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  TUESDAY: { enabled: true, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  WEDNESDAY: { enabled: true, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  THURSDAY: { enabled: true, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  FRIDAY: { enabled: true, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  SATURDAY: { enabled: false, slots: [{ startTime: '09:00', endTime: '17:00' }] },
  SUNDAY: { enabled: false, slots: [{ startTime: '09:00', endTime: '17:00' }] },
};

const BusinessHours: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState<BusinessHoursSyncReport | null>(null);
  const [existingHours, setExistingHours] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const [config, setConfig] = useState<BusinessHoursConfig>({
    timezone: 'Asia/Kolkata',
    schedule: defaultSchedule,
    enabled: true,
    afterHoursMessage: 'We are currently closed. Please call back during business hours.',
    afterHoursAction: 'HANGUP',
    afterHoursTransferNumber: '',
    afterHoursAudioUrl: '',
  });

  // Business admins get their own organization implicitly; provider-level roles
  // choose one. See useScopedOrgId — before it, every role outside the chooser
  // list was left with no organization at all and a dead page.
  const { selectedOrgId, setSelectedOrgId, showOrgFilter } = useScopedOrgId();

  // Load business hours when org changes
  useEffect(() => {
    if (selectedOrgId) {
      loadBusinessHours();
    }
  }, [selectedOrgId]);

  const loadBusinessHours = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    try {
      const response = await businessHoursApi.get(selectedOrgId);

      if (response.success && response.data) {
        // Transform backend data to frontend format
        const data = response.data;
        const schedule = { ...defaultSchedule };

        // Map hoursByDay to schedule
        if (data.hoursByDay) {
          DAYS.forEach(day => {
            // The API sends a list per day. A bare object is still accepted, so
            // a stale response cannot blank the whole week.
            const raw = data.hoursByDay[day];
            const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
            const slots = list
              .filter((h: any) => h && h.startTime && h.endTime)
              .map((h: any) => ({
                startTime: String(h.startTime).slice(0, 5),
                endTime: String(h.endTime).slice(0, 5),
              }));
            schedule[day] = slots.length
              ? { enabled: true, slots }
              : { enabled: false, slots: [{ startTime: '09:00', endTime: '17:00' }] };
          });
        }

        setConfig({
          id: data.id,
          timezone: data.timezone || 'Asia/Kolkata',
          schedule,
          enabled: data.enabled !== false,
          afterHoursMessage: data.afterHoursMessage || '',
          afterHoursAction: data.afterHoursAction || 'HANGUP',
          afterHoursTransferNumber: data.afterHoursTransferNumber || '',
          afterHoursAudioUrl: data.afterHoursAudioUrl || '',
        });
        setExistingHours(true);
      } else {
        // No existing hours, reset to default
        setConfig({
          timezone: 'Asia/Kolkata',
          schedule: defaultSchedule,
          enabled: true,
          afterHoursMessage: 'We are currently closed. Please call back during business hours.',
          afterHoursAction: 'HANGUP',
          afterHoursTransferNumber: '',
          afterHoursAudioUrl: '',
        });
        setExistingHours(false);
      }
    } catch (error) {
      showNotification('error', 'Failed to load business hours');
      setExistingHours(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId) {
      showNotification('error', 'Please select an organization first');
      return;
    }

    setSaving(true);

    try {
      // Transform frontend format to backend format
      const hoursByDay: Record<string, { startTime: string; endTime: string }[] | null> = {};

      DAYS.forEach(day => {
        const d = config.schedule[day];
        hoursByDay[day] = d.enabled
          ? d.slots.map(s => ({ startTime: s.startTime + ':00', endTime: s.endTime + ':00' }))
          : null;
      });

      const payload = {
        hoursByDay,
        timezone: config.timezone,
        enabled: config.enabled,
        afterHoursMessage: config.afterHoursMessage || undefined,
        afterHoursAudioUrl: config.afterHoursAudioUrl || undefined,
        afterHoursAction: config.afterHoursAction,
        afterHoursTransferNumber: config.afterHoursTransferNumber || undefined,
      };

      const response = existingHours
        ? await businessHoursApi.update(selectedOrgId, payload)
        : await businessHoursApi.create(selectedOrgId, payload);

      if (response.success) {
        // The server's message carries the runtime-push outcome ("...and pushed
        // to the voice runtime", or how many windows it would not take). A fixed
        // "saved successfully" here would hide the half that matters.
        showNotification('success', (response as any).message || 'Business hours saved successfully!');
        setExistingHours(true);
        if (response.data?.id) {
          setConfig(prev => ({ ...prev, id: response.data?.id }));
        }
      } else {
        showNotification('error', response.error || 'Failed to save business hours');
      }
    } catch (error) {
      showNotification('error', 'Network error saving business hours');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Push to the voice runtime on demand.
   *
   * Saving already pushes; this is for retrying after the runtime was down, and
   * for seeing the per-day detail when it would not take every window.
   */
  const handleSync = async () => {
    if (!selectedOrgId) return;
    setSyncing(true);
    setSyncReport(null);
    try {
      const res = await businessHoursApi.syncToRuntime(selectedOrgId);
      if (res.success && res.data) {
        setSyncReport(res.data);
        showNotification(res.data.faithful ? 'success' : 'error',
          res.data.faithful
            ? 'The voice runtime holds every window'
            : `The voice runtime holds ${res.data.windowsConfirmed} of ${res.data.windowsSent} window(s)`);
      } else {
        showNotification('error', res.error || 'Could not push the hours');
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedOrgId || !existingHours) return;

    if (!confirm('Are you sure you want to delete business hours configuration?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await businessHoursApi.delete(selectedOrgId);
      if (response.success) {
        showNotification('success', 'Business hours deleted successfully');
        setConfig({
          timezone: 'Asia/Kolkata',
          schedule: defaultSchedule,
          enabled: true,
          afterHoursMessage: 'We are currently closed. Please call back during business hours.',
          afterHoursAction: 'HANGUP',
          afterHoursTransferNumber: '',
          afterHoursAudioUrl: '',
        });
        setExistingHours(false);
      } else {
        showNotification('error', response.error || 'Failed to delete business hours');
      }
    } catch (error) {
      showNotification('error', 'Network error deleting business hours');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const toggleDay = (day: typeof DAYS[number]) => {
    setConfig(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          enabled: !prev.schedule[day].enabled,
        },
      },
    }));
  };

  const updateTimeSlot = (
    day: typeof DAYS[number], index: number, field: 'startTime' | 'endTime', value: string,
  ) => {
    setConfig(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule[day],
          slots: prev.schedule[day].slots.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
        },
      },
    }));
  };

  /** One more window on that day, starting where the last one ended. */
  const addSlot = (day: typeof DAYS[number]) => {
    setConfig(prev => {
      const slots = prev.schedule[day].slots;
      const last = slots[slots.length - 1];
      return {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: {
            ...prev.schedule[day],
            slots: [...slots, { startTime: last?.endTime || '09:00', endTime: '18:00' }],
          },
        },
      };
    });
  };

  /**
   * Removing the last window does not close the day — unticking it does.
   * Otherwise a stray click would silently shut a day, and the difference
   * between "closed" and "misconfigured" is what somebody reads later.
   */
  const removeSlot = (day: typeof DAYS[number], index: number) => {
    setConfig(prev => {
      const slots = prev.schedule[day].slots.filter((_, i) => i !== index);
      return {
        ...prev,
        schedule: {
          ...prev.schedule,
          [day]: {
            ...prev.schedule[day],
            slots: slots.length ? slots : [{ startTime: '09:00', endTime: '17:00' }],
          },
        },
      };
    });
  };

  const copyToAllWeekdays = (day: typeof DAYS[number]) => {
    const sourceSchedule = config.schedule[day];
    setConfig(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        MONDAY: { ...sourceSchedule },
        TUESDAY: { ...sourceSchedule },
        WEDNESDAY: { ...sourceSchedule },
        THURSDAY: { ...sourceSchedule },
        FRIDAY: { ...sourceSchedule },
      },
    }));
  };

  const copyToAllDays = (day: typeof DAYS[number]) => {
    const sourceSchedule = config.schedule[day];
    setConfig(prev => ({
      ...prev,
      schedule: {
        MONDAY: { ...sourceSchedule },
        TUESDAY: { ...sourceSchedule },
        WEDNESDAY: { ...sourceSchedule },
        THURSDAY: { ...sourceSchedule },
        FRIDAY: { ...sourceSchedule },
        SATURDAY: { ...sourceSchedule },
        SUNDAY: { ...sourceSchedule },
      },
    }));
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <Clock className="w-6 h-6 text-indigo-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Business Hours Configuration</h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Configure operating hours for your telephony system
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {existingHours && (
            <button
              onClick={handleDelete}
              disabled={loading || saving || !selectedOrgId}
              className="flex items-center space-x-2 px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          )}
          {existingHours && (
            <button
              onClick={handleSync}
              disabled={loading || syncing || !selectedOrgId}
              title="Saving already pushes these hours. Use this to retry, or to see which windows the runtime would not take."
              className="flex items-center space-x-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PhoneCall className="w-4 h-4" />
              <span>{syncing ? 'Pushing...' : 'Push to voice runtime'}</span>
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={loading || saving || !selectedOrgId}
            className="tl-btn tl-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : existingHours ? 'Update Configuration' : 'Save Configuration'}</span>
          </button>
        </div>
      </div>

      {/* What the runtime actually kept. Shown only when it disagrees with cpass:
          a green tick nobody reads is worse than nothing, and the mismatch is the
          only outcome that needs a person. */}
      {syncReport && !syncReport.faithful && (
        <div className="mb-6 border border-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            The voice runtime holds {syncReport.windowsConfirmed} of {syncReport.windowsSent} window(s).
            Campaign dialling may not follow these hours.
          </p>
          <ul className="mt-2 text-xs text-amber-800 dark:text-amber-300 list-disc pl-5 space-y-1">
            {syncReport.days.filter(d => d.detail).map(d => (
              <li key={d.day}>{d.day}: {d.detail}</li>
            ))}
            {syncReport.failures.map((f, i) => (
              <li key={`f${i}`}>{f}</li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
            Calls cpass places itself — including reply rules — always follow the hours above.
          </p>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`flex items-center space-x-2 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Filter */}
      {showOrgFilter && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <OrganizationFilter
            value={selectedOrgId}
            onChange={setSelectedOrgId}
            className="w-full md:w-96"
            requireSelection={true}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show message if no org selected */}
      {showOrgFilter && !selectedOrgId && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <p className="text-blue-800 dark:text-blue-300 text-center">
            Please select a business from the dropdown above to configure business hours.
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading && selectedOrgId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading business hours...</p>
          </div>
        </div>
      )}

      {/* Main Form */}
      {selectedOrgId && !loading && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Business Hours Status</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Enable or disable business hours checking</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>

          {/* Basic Settings */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Timezone</h2>
            <select
              value={config.timezone}
              onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
              className="w-full md:w-80 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="Asia/Kolkata">India (IST)</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Dubai">Dubai (GST)</option>
              <option value="Asia/Singapore">Singapore (SGT)</option>
              <option value="Australia/Sydney">Sydney (AEST)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>

          {/* Weekly Schedule */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Weekly Schedule</h2>
            <div className="space-y-4">
              {DAYS.map((day) => (
                <div key={day} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={config.schedule[day].enabled}
                        onChange={() => toggleDay(day)}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                      />
                      <label className="text-sm font-medium text-gray-900 dark:text-white w-24">
                        {DAY_LABELS[day]}
                      </label>

                      {config.schedule[day].enabled && (
                        <div className="flex flex-col gap-2">
                          {config.schedule[day].slots.map((slot, i) => (
                            <div key={i} className="flex items-center space-x-3">
                              <input
                                type="time"
                                value={slot.startTime}
                                onChange={(e) => updateTimeSlot(day, i, 'startTime', e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                              <span className="text-gray-500 dark:text-gray-400">to</span>
                              <input
                                type="time"
                                value={slot.endTime}
                                onChange={(e) => updateTimeSlot(day, i, 'endTime', e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                              {config.schedule[day].slots.length > 1 && (
                                <button type="button"
                                        onClick={() => removeSlot(day, i)}
                                        className="text-xs text-red-600 px-2 py-1">
                                  remove
                                </button>
                              )}
                              {i === config.schedule[day].slots.length - 1 && (
                                <button type="button"
                                        onClick={() => addSlot(day)}
                                        className="text-xs text-indigo-600 px-2 py-1">
                                  + add hours
                                </button>
                              )}
                            </div>
                          ))}
                          {config.schedule[day].slots.length > 1 && (
                            <span className="text-[11px] text-gray-400">
                              Closed between these windows.
                            </span>
                          )}
                        </div>
                      )}

                      {!config.schedule[day].enabled && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 italic">Closed</span>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => copyToAllWeekdays(day)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center space-x-1 px-2 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                        title="Copy to Mon-Fri"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Weekdays</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => copyToAllDays(day)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center space-x-1 px-2 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                        title="Copy to all days"
                      >
                        <Copy className="w-3 h-3" />
                        <span>All</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* After Hours Behavior */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <PhoneCall className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">After Hours Behavior</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Action when outside business hours
                </label>
                <select
                  value={config.afterHoursAction}
                  onChange={(e) => setConfig({ ...config, afterHoursAction: e.target.value as AfterHoursAction })}
                  className="w-full md:w-80 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="HANGUP">Play Message & Hangup</option>
                  <option value="VOICEMAIL">Send to Voicemail</option>
                  <option value="TRANSFER">Transfer to Number</option>
                  <option value="CUSTOM">Custom IVR Flow</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Closed Message (TTS)
                </label>
                <textarea
                  value={config.afterHoursMessage}
                  onChange={(e) => setConfig({ ...config, afterHoursMessage: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Message to play when outside business hours..."
                />
              </div>

              {config.afterHoursAction === 'TRANSFER' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transfer Number
                  </label>
                  <input
                    type="tel"
                    value={config.afterHoursTransferNumber}
                    onChange={(e) => setConfig({ ...config, afterHoursTransferNumber: e.target.value })}
                    className="w-full md:w-80 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="+1234567890"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Audio File URL (Optional)
                </label>
                <input
                  type="url"
                  value={config.afterHoursAudioUrl}
                  onChange={(e) => setConfig({ ...config, afterHoursAudioUrl: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com/closed-message.mp3"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  If provided, this audio will be played instead of TTS message
                </p>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <h3 className="font-medium text-blue-900 dark:text-blue-300">Smart Routing</h3>
              </div>
              <p className="text-sm text-blue-700 dark:text-blue-400">
                Calls will be automatically handled based on business hours. IVR flows can check business hours status.
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Calendar className="w-5 h-5 text-green-600" />
                <h3 className="font-medium text-green-900 dark:text-green-300">Timezone Aware</h3>
              </div>
              <p className="text-sm text-green-700 dark:text-green-400">
                All time calculations are performed in your configured timezone.
              </p>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default BusinessHours;
